/**
 * @sazeo/sdk — CRM-Contacts hooks
 *
 * TanStack Query wrappers for the crm-contacts addon. All calls route through
 * the Edge Function at `/addons/crm-contacts/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Contact {
  id: string;
  tenant_id: string;
  lead_id: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  role_title: string | null;
  company: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deal_stage?: string | null;
  deal_value?: number | null;
}

export interface ContactHistoryEntry {
  id: string;
  tenant_id: string;
  contact_id: string;
  kind: 'call' | 'email' | 'meeting' | 'note' | 'other';
  summary: string;
  occurred_at: string;
  created_by: string | null;
  created_at: string;
}

export interface ContactCreateInput {
  tenant_id: string;
  lead_id?: string | null;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  role_title?: string | null;
  company?: string | null;
  notes?: string | null;
}

export interface ContactUpdateInput {
  lead_id?: string | null;
  full_name?: string;
  email?: string | null;
  phone?: string | null;
  role_title?: string | null;
  company?: string | null;
  notes?: string | null;
  deal_stage?: string | null;
  deal_value?: number | null;
}

export interface ContactHistoryCreateInput {
  kind: 'call' | 'email' | 'meeting' | 'note' | 'other';
  summary: string;
  occurred_at?: string;
}

export interface ContactFilter {
  search?: string;
  lead_id?: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const contactKeys = {
  all: ['crm-contacts'] as const,
  list: (tenantId: string, filter?: ContactFilter) =>
    [...contactKeys.all, 'list', tenantId, filter ?? null] as const,
  detail: (id: string) => [...contactKeys.all, 'detail', id] as const,
  history: (contactId: string) => [...contactKeys.all, 'history', contactId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers (mirror crm.ts pattern)
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

function buildQuery(filter?: ContactFilter): string {
  if (!filter) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useContacts(
  supabase: SupabaseClientLike,
  tenantId: string,
  filter?: ContactFilter,
) {
  return useQuery({
    queryKey: contactKeys.list(tenantId, filter),
    queryFn: () =>
      apiFetch<Contact[]>(supabase, `addons/crm-contacts/contacts${buildQuery(filter)}`),
    enabled: Boolean(tenantId),
  });
}

export function useContact(supabase: SupabaseClientLike, id: string) {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => apiFetch<Contact>(supabase, `addons/crm-contacts/contacts/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateContact(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContactCreateInput) =>
      apiFetch<Contact>(supabase, 'addons/crm-contacts/contacts', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useUpdateContact(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ContactUpdateInput }) =>
      apiFetch<Contact>(supabase, `addons/crm-contacts/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: contactKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useDeleteContact(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/crm-contacts/contacts/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: contactKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useContactHistory(supabase: SupabaseClientLike, contactId: string) {
  return useQuery({
    queryKey: contactKeys.history(contactId),
    queryFn: () =>
      apiFetch<ContactHistoryEntry[]>(
        supabase,
        `addons/crm-contacts/contacts/${contactId}/history`,
      ),
    enabled: Boolean(contactId),
  });
}

export function useAddContactHistoryEntry(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      input,
    }: {
      contactId: string;
      input: ContactHistoryCreateInput;
    }) =>
      apiFetch<ContactHistoryEntry>(supabase, `addons/crm-contacts/contacts/${contactId}/history`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: contactKeys.history(vars.contactId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Pipeline (Kanban) hooks
// ---------------------------------------------------------------------------

export interface CRMPipelineColumn {
  stage: string;
  contacts: Contact[];
}

export const PIPELINE_STAGES = [
  'lead',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
] as const;

export type DealStage = (typeof PIPELINE_STAGES)[number];

export function useCRMPipeline(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: ['crm', 'pipeline', tenantId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('crm_contacts')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('deal_value', { ascending: false, nullsLast: true });
      if (error) throw error;
      return PIPELINE_STAGES.map((stage) => ({
        stage,
        contacts: (data as Contact[]).filter((c) => (c.deal_stage ?? 'lead') === stage),
      })) as CRMPipelineColumn[];
    },
    enabled: Boolean(tenantId),
  });
}

// ---------------------------------------------------------------------------
// International Contacts — types
// ---------------------------------------------------------------------------

export interface InternationalContact {
  id: string;
  tenant_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role_title: string | null;
  company: string | null;
  notes: string | null;
  country: string | null;
  timezone: string | null;
  preferred_language: string | null;
  linkedin_url: string | null;
  website: string | null;
  source: 'business_card' | 'linkedin' | 'event' | 'manual';
  card_image_url: string | null;
  card_scanned_at: string | null;
  deal_stage: string | null;
  deal_value: number | null;
  parent_contact_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface InternationalContactCreateInput {
  tenant_id: string;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  role_title?: string | null;
  company?: string | null;
  notes?: string | null;
  country?: string | null;
  timezone?: string | null;
  preferred_language?: string | null;
  linkedin_url?: string | null;
  website?: string | null;
  source?: 'business_card' | 'linkedin' | 'event' | 'manual';
  card_image_url?: string | null;
  card_scanned_at?: string | null;
  deal_stage?: string | null;
  deal_value?: number | null;
  parent_contact_id?: string | null;
}

export interface InternationalContactUpdateInput
  extends Partial<Omit<InternationalContactCreateInput, 'tenant_id'>> {}

export interface BusinessCardScanResult {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  role_title: string | null;
  company: string | null;
  website: string | null;
  linkedin_url: string | null;
  country: string | null;
  preferred_language: string | null;
  card_image_url: string | null;
}

// ---------------------------------------------------------------------------
// International Contacts — query keys
// ---------------------------------------------------------------------------

export const intlContactKeys = {
  all: ['crm-intl-contacts'] as const,
  list: (tenantId: string, filter?: ContactFilter) =>
    [...intlContactKeys.all, 'list', tenantId, filter ?? null] as const,
  detail: (id: string) => [...intlContactKeys.all, 'detail', id] as const,
} as const;

// ---------------------------------------------------------------------------
// International Contacts — hooks
// ---------------------------------------------------------------------------

export function useInternationalContacts(
  supabase: SupabaseClientLike,
  tenantId: string,
  filter?: ContactFilter,
) {
  return useQuery({
    queryKey: intlContactKeys.list(tenantId, filter),
    queryFn: () =>
      apiFetch<InternationalContact[]>(
        supabase,
        `addons/crm-contacts/international${buildQuery(filter)}`,
      ),
    enabled: Boolean(tenantId),
  });
}

export function useCreateInternationalContact(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InternationalContactCreateInput) =>
      apiFetch<InternationalContact>(supabase, 'addons/crm-contacts/international', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: intlContactKeys.all });
    },
  });
}

export function useUpdateInternationalContact(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: InternationalContactUpdateInput }) =>
      apiFetch<InternationalContact>(supabase, `addons/crm-contacts/international/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: intlContactKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: intlContactKeys.all });
    },
  });
}

export function useDeleteInternationalContact(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/crm-contacts/international/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: intlContactKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: intlContactKeys.all });
    },
  });
}

export function useScanBusinessCard(supabase: SupabaseClientLike) {
  return useMutation({
    mutationFn: async (file: File) => {
      const headers = await getAuthHeaders(supabase);
      const body = new FormData();
      body.append('image', file);
      const res = await fetch('/functions/v1/addons/crm-contacts/scan-business-card', {
        method: 'POST',
        headers,
        body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: res.statusText } }));
        throw new Error((err as { error?: { message?: string } }).error?.message ?? res.statusText);
      }
      return res.json() as Promise<BusinessCardScanResult>;
    },
  });
}

export function useUpdateContactStage(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      deal_stage,
      deal_value,
    }: {
      contactId: string;
      deal_stage: string;
      deal_value?: number | null;
    }) =>
      apiFetch<Contact>(supabase, `addons/crm-contacts/contacts/${contactId}`, {
        method: 'PATCH',
        body: JSON.stringify({ deal_stage, ...(deal_value !== undefined ? { deal_value } : {}) }),
      }),
    onMutate: async ({ contactId, deal_stage }) => {
      await qc.cancelQueries({ queryKey: ['crm', 'pipeline', tenantId] });
      const previous = qc.getQueryData<CRMPipelineColumn[]>(['crm', 'pipeline', tenantId]);
      qc.setQueryData<CRMPipelineColumn[]>(['crm', 'pipeline', tenantId], (old) => {
        if (!old) return old;
        let moved: Contact | undefined;
        const removed = old.map((col) => {
          const next = col.contacts.filter((c) => {
            if (c.id === contactId) {
              moved = c;
              return false;
            }
            return true;
          });
          return { ...col, contacts: next };
        });
        if (!moved) return removed;
        return removed.map((col) =>
          col.stage === deal_stage
            ? { ...col, contacts: [...col.contacts, { ...moved!, deal_stage }] }
            : col,
        );
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['crm', 'pipeline', tenantId], ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['crm', 'pipeline', tenantId] });
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}
